import { useState } from 'react';
import { LICENSE_KEY_INPUT_ID, useLicense } from './context';
import { LicenseKeyForm } from './LicenseKeyForm';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PRO_PLAN, proCtaLabel } from '../../config/pricing';
import { track } from '../analytics/client';
import './license.css';

/**
 * Persistent License panel (spec §7.5) shown in the rail. Surfaces the current
 * tier and lets the user activate a key or remove an active license. Locked Pro
 * elements elsewhere link here via their inline upgrade prompt.
 *
 * `manageUrl` is the Lemon Squeezy customer-portal link (SP2, cue-forge-332.5):
 * shown to subscribers (active or lapsed) so they can update payment, cancel, or
 * resume. It defaults to the build-time `PRO_PLAN.manageUrl` and is injectable
 * for tests; when empty (the default in dev/CI) no link is rendered.
 */
export function LicensePanel({ manageUrl = PRO_PLAN.manageUrl }: { manageUrl?: string } = {}) {
  const { isPro, status, deactivate } = useLicense();
  // Removing a license is destructive (it re-locks every Pro feature), so it
  // goes through the same confirm step as the app's other destructive actions
  // (cue-forge-w28.16) rather than firing on a single click.
  const [confirming, setConfirming] = useState(false);
  const lapseNotice =
    !isPro && status.state === 'expired'
      ? 'Your Pro subscription expired. Renew to restore Pro.'
      : !isPro && status.state === 'cancelled'
        ? 'Your Pro subscription was cancelled.'
        : null;
  // Only subscribers (current or lapsed) have something to manage; never show a
  // billing link to a brand-new free user.
  const showManage = manageUrl !== '' && (isPro || lapseNotice !== null);
  return (
    <section className="cf-license" aria-label="License">
      <h2 className="cf-license__title">License</h2>
      <p className="cf-license__status">
        Status: <strong>{isPro ? 'Pro' : 'Free'}</strong>
      </p>
      {lapseNotice && (
        <p className="cf-license__notice" role="status">
          {lapseNotice}
        </p>
      )}
      {isPro ? (
        <button type="button" className="cf-btn" onClick={() => setConfirming(true)}>
          Remove license
        </button>
      ) : (
        <>
          <LicenseKeyForm inputId={LICENSE_KEY_INPUT_ID} />
          {/* The single trial CTA for the whole app (cue-forge-crm): locked
              features elsewhere route here instead of each repeating the pitch,
              so the honest "start your free trial" copy stays reachable without
              stacking full pricing cards across the Studio. Rendered only when a
              hosted checkout is configured (matches the full card's guard). */}
          {PRO_PLAN.checkoutUrl !== '' && (
            <p className="cf-license__trial">
              <a
                href={PRO_PLAN.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('trial_click', {})}
              >
                {proCtaLabel(PRO_PLAN)}
              </a>
            </p>
          )}
        </>
      )}
      {confirming && (
        <ConfirmDialog
          title="Remove license?"
          message="This locks every Pro feature on this device. You'll need to enter your license key again to re-activate Pro."
          confirmLabel="Remove license"
          onConfirm={() => {
            setConfirming(false);
            deactivate();
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
      {showManage && (
        <p className="cf-license__manage">
          <a href={manageUrl} target="_blank" rel="noopener noreferrer">
            Manage subscription
          </a>
        </p>
      )}
    </section>
  );
}
